import { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { RefreshCw, Layers, HelpCircle, GitBranch, Play, Info, Target } from 'lucide-react';
import { useTutorStore } from '@shared/state';
import { trackEvent, getBaseProperties } from '@shared/services/analytics';
import { ANALYTICS_EVENTS } from '@shared/constants';
import type { TutorMode } from '@shared/types/tutor';

type TutorQueryEventMode =
  | 'explain_differently' | 'go_deeper' | 'test_me' | 'connect_this' | 'show_me' | 'freeform';

// Map a tutor mode to the analytics event's mode union (drill_me has no event
// value of its own — it's a test variant; adaptation isn't a learning query).
function toEventMode(mode: TutorMode): TutorQueryEventMode {
  switch (mode) {
    case 'explain_differently':
    case 'go_deeper':
    case 'test_me':
    case 'connect_this':
    case 'show_me':
      return mode;
    default:
      return mode === 'drill_me' ? 'test_me' : 'freeform';
  }
}

const modes: Array<{ mode: TutorMode; icon: typeof RefreshCw; label: string; tooltip: string; description: string }> = [
  {
    mode: 'explain_differently', icon: RefreshCw, label: 'Explain',
    tooltip: 'Explain this another way',
    description: 'Not clicking? Your Tutor will re-explain the concept using a completely different format and approach — visual instead of text, example instead of theory, or an everyday analogy.',
  },
  {
    mode: 'go_deeper', icon: Layers, label: 'Deeper',
    tooltip: 'Tell me more',
    description: 'Want more detail? Your Tutor will expand on the current concept — extra examples, edge cases, exceptions, and connections to related ideas. One level deeper than what you\'re seeing.',
  },
  {
    mode: 'test_me', icon: HelpCircle, label: 'Test Me',
    tooltip: 'Test my understanding',
    description: 'Think you\'ve got it? Your Tutor will generate a comprehension question calibrated to your profile — then score your answer and give specific feedback.',
  },
  {
    mode: 'drill_me', icon: Target, label: 'Drill',
    tooltip: 'Run a short drill session',
    description: 'Practise hard. Your Tutor will run a 5-question drill on this concept — one question at a time, scored as you go, with a summary at the end showing where you\'re strong and where to keep practising.',
  },
  {
    mode: 'connect_this', icon: GitBranch, label: 'Connect',
    tooltip: 'How does this connect?',
    description: 'See the bigger picture. Your Tutor will show how this concept connects to others you\'ve already covered and preview what\'s coming up — with a mini concept map.',
  },
  {
    mode: 'show_me', icon: Play, label: 'Watch',
    tooltip: 'Watch a video on this',
    description: 'Prefer to watch? Your Tutor will find short, high-quality YouTube videos on this concept. If nothing good exists, it\'ll create a visual explanation instead.',
  },
];

export function TutorModeButtons() {
  const activeMode = useTutorStore((s) => s.activeMode);
  const setMode = useTutorStore((s) => s.setMode);
  const sendQuery = useTutorStore((s) => s.sendQuery);
  const isLoading = useTutorStore((s) => s.isLoading);
  const [hoveredMode, setHoveredMode] = useState<TutorMode | null>(null);
  const [pinnedMode, setPinnedMode] = useState<TutorMode | null>(null);

  function handleModeClick(mode: TutorMode) {
    if (isLoading) return;
    setHoveredMode(null);
    setPinnedMode(null);
    if (activeMode === mode) {
      setMode(null);
    } else {
      setMode(mode);
      const autoQueries: Record<string, string> = {
        explain_differently: 'Explain this differently',
        go_deeper: 'Go deeper on this',
        test_me: 'Test my understanding',
        drill_me: 'Start a drill session on this concept — 5 questions, one at a time, scored as I go.',
        connect_this: 'How does this connect to other concepts?',
        show_me: 'Show me a video on this',
      };
      const autoQuery = autoQueries[mode];
      // Behavioural signal (weight 0.7) — carries the mode so the engine can read
      // intent (e.g. repeated go_deeper ⇒ depth too shallow). Skip profile chats.
      const { pathId, activeNodeId, currentSession, adaptationMode } = useTutorStore.getState();
      if (!adaptationMode && pathId) {
        trackEvent({
          name: ANALYTICS_EVENTS.TUTOR_QUERY_SENT,
          properties: {
            ...getBaseProperties(),
            pathId,
            nodeId: activeNodeId,
            mode: toEventMode(mode),
            conceptId: null,
            queryLength: autoQuery.length,
            queryNumber: (currentSession?.queriesInSession ?? 0) + 1,
          },
        }).catch(() => {});
      }
      sendQuery(autoQuery);
    }
  }

  function toggleInfo(mode: TutorMode, e: React.MouseEvent) {
    e.stopPropagation();
    setPinnedMode((current) => (current === mode ? null : mode));
  }

  const visibleMode = hoveredMode ?? pinnedMode;
  const visibleInfo = modes.find((m) => m.mode === visibleMode);

  return (
    <div className="relative">
      <div className="flex flex-wrap gap-1 px-3 py-2">
        {modes.map(({ mode, icon: Icon, label }) => {
          const isActive = activeMode === mode;
          const isPinned = pinnedMode === mode;
          return (
            <div key={mode} className="relative flex items-center shrink-0">
              <motion.button
                onClick={() => handleModeClick(mode)}
                onMouseEnter={() => setHoveredMode(mode)}
                onMouseLeave={() => setHoveredMode(null)}
                disabled={isLoading}
                className={`flex items-center gap-1.5 px-3 py-1.5 rounded-pill text-xs font-body font-semibold
                  transition-all duration-150 cursor-pointer whitespace-nowrap
                  ${isActive
                    ? 'bg-indigo-500 text-white shadow-glow-indigo'
                    : 'bg-transparent border border-dark-border text-content-muted hover:text-content hover:border-dark-border-light'
                  }
                  disabled:opacity-40 disabled:cursor-not-allowed
                `}
                whileTap={{ scale: 0.95 }}
              >
                <Icon size={13} />
                {label}
              </motion.button>
              {/* Touch-accessible info affordance */}
              <button
                type="button"
                onClick={(e) => toggleInfo(mode, e)}
                aria-label={`What does ${label} do?`}
                aria-expanded={isPinned}
                className={`ml-0.5 sm:hidden flex items-center justify-center w-5 h-5 rounded-full cursor-pointer transition-colors
                  ${isPinned ? 'text-indigo-400 bg-indigo-500/15' : 'text-content-dim hover:text-content'}
                `}
              >
                <Info size={11} />
              </button>
            </div>
          );
        })}
      </div>

      {/* Description popup — hover on desktop, tapped info-button on mobile */}
      <AnimatePresence>
        {visibleInfo && (
          <motion.div
            initial={{ opacity: 0, y: -4 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -4 }}
            transition={{ duration: 0.15 }}
            className="absolute left-3 right-3 top-full mt-1 z-20 p-3 rounded-lg bg-dark-elevated border border-dark-border shadow-modal"
          >
            <p className="text-xs text-indigo-400 font-body font-semibold mb-1">{visibleInfo.tooltip}</p>
            <p className="text-xs text-content-secondary font-body leading-relaxed">{visibleInfo.description}</p>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}
